import type { QuerySpec } from '@helios/powersync-client-core';

/** Row shape from the app_config PowerSync table. */
export interface AppConfigRow {
  id: string;
  org_name: string | null;
  org_localname: string | null;
  logo_url: string | null;
  country: string | null;
  region: string | null;
  language: string | null;
  currency: string | null;
  org_address: string | null;
  org_phone: string | null;
  org_mobile: string | null;
  org_email: string | null;
  sms_sender_name: string | null;
  extra_json: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** Allowed values for patient_form_layout_mode. */
export type PatientFormLayoutMode = 'wizard' | 'single_page';

/**
 * QuerySpec for the singleton app_config row.
 */
export function qAppConfig(): QuerySpec<[]> {
  return {
    key: ['app-config'] as const,
    sql: `SELECT * FROM app_config WHERE id = 'app' LIMIT 1`,
    params: [],
    watch: ['app_config'],
  };
}

/**
 * Extract patient_form_layout_mode from an app_config row.
 * Returns 'wizard' if missing, NULL, or invalid.
 */
export function getPatientFormLayoutMode(row: AppConfigRow | undefined): PatientFormLayoutMode {
  if (!row?.extra_json) return 'wizard';
  try {
    const parsed = JSON.parse(row.extra_json);
    if (parsed?.patient_form_layout_mode === 'single_page') return 'single_page';
  } catch {
    // invalid JSON, fall through
  }
  return 'wizard';
}

// ─── PRD.230 — Regional settings (stored under app_config.extra_json.regional_settings) ───

export type MoneyCurrencyDisplay = 'symbol' | 'code';
export type MoneyCurrencyPosition = 'prefix' | 'suffix';

export interface MoneyFormatConfig {
  use_thousands_separator: boolean;
  thousands_separator: string;
  decimal_separator: string;
  decimal_places: number;
  currency_display: MoneyCurrencyDisplay;
  currency_symbol: string;
  currency_position: MoneyCurrencyPosition;
}

export interface IdTypeEntry {
  code: string;
  label: string;
  enabled: boolean;
}

export interface RegionalSettings {
  default_mobile_country_code: string;
  money_format: MoneyFormatConfig;
  id_types: IdTypeEntry[];
}

/** PH defaults used when nothing has been saved yet. */
export const PH_DEFAULT_REGIONAL_SETTINGS: RegionalSettings = {
  default_mobile_country_code: '+63',
  money_format: {
    use_thousands_separator: true,
    thousands_separator: ',',
    decimal_separator: '.',
    decimal_places: 2,
    currency_display: 'symbol',
    currency_symbol: '₱',
    currency_position: 'prefix',
  },
  id_types: [
    { code: 'philhealth', label: 'PhilHealth ID', enabled: true },
    { code: 'national_id', label: 'National ID', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
    { code: 'prc', label: 'PRC ID', enabled: true },
    { code: 'senior_citizen', label: 'Senior Citizen ID', enabled: true },
    { code: 'pwd', label: 'PWD ID', enabled: true },
    { code: 'sss', label: 'SSS ID', enabled: true },
    { code: 'gsis', label: 'GSIS ID', enabled: true },
    { code: 'tin', label: 'TIN ID', enabled: true },
    { code: 'umid', label: 'UMID', enabled: true },
  ],
};

/**
 * Parse `extra_json` (text from PowerSync SQLite, object from server)
 * into a plain object. Falls back to {} on any malformed input.
 */
function parseExtraJson(extraJson: unknown): Record<string, unknown> {
  if (extraJson == null) return {};
  if (typeof extraJson === 'object' && !Array.isArray(extraJson)) {
    return extraJson as Record<string, unknown>;
  }
  if (typeof extraJson === 'string' && extraJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(extraJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* fall through */
    }
  }
  return {};
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function mergeMoneyFormat(raw: unknown): MoneyFormatConfig {
  const defaults = PH_DEFAULT_REGIONAL_SETTINGS.money_format;
  if (!isObject(raw)) return defaults;
  const decimalPlaces =
    typeof raw.decimal_places === 'number' && Number.isFinite(raw.decimal_places)
      ? Math.min(4, Math.max(0, Math.trunc(raw.decimal_places)))
      : defaults.decimal_places;
  const currencyDisplay: MoneyCurrencyDisplay = raw.currency_display === 'code' ? 'code' : 'symbol';
  const currencyPosition: MoneyCurrencyPosition =
    raw.currency_position === 'suffix' ? 'suffix' : 'prefix';
  return {
    use_thousands_separator:
      typeof raw.use_thousands_separator === 'boolean'
        ? raw.use_thousands_separator
        : defaults.use_thousands_separator,
    thousands_separator:
      typeof raw.thousands_separator === 'string'
        ? raw.thousands_separator
        : defaults.thousands_separator,
    decimal_separator:
      typeof raw.decimal_separator === 'string'
        ? raw.decimal_separator
        : defaults.decimal_separator,
    decimal_places: decimalPlaces,
    currency_display: currencyDisplay,
    currency_symbol:
      typeof raw.currency_symbol === 'string' ? raw.currency_symbol : defaults.currency_symbol,
    currency_position: currencyPosition,
  };
}

function mergeIdTypes(raw: unknown): IdTypeEntry[] {
  if (!Array.isArray(raw)) return PH_DEFAULT_REGIONAL_SETTINGS.id_types.map((t) => ({ ...t }));
  const seen = new Set<string>();
  const out: IdTypeEntry[] = [];
  for (const item of raw) {
    if (!isObject(item)) continue;
    const code = typeof item.code === 'string' ? item.code.trim() : '';
    const label = typeof item.label === 'string' ? item.label.trim() : '';
    if (!code || !label) continue;
    if (seen.has(code)) continue;
    seen.add(code);
    out.push({
      code,
      label,
      enabled: typeof item.enabled === 'boolean' ? item.enabled : true,
    });
  }
  return out.length > 0 ? out : PH_DEFAULT_REGIONAL_SETTINGS.id_types.map((t) => ({ ...t }));
}

/**
 * Extract `regional_settings` from an app_config row, applying PH defaults
 * for any missing or malformed fields. Always returns a fully populated
 * `RegionalSettings` object.
 */
export function getRegionalSettings(row: AppConfigRow | null | undefined): RegionalSettings {
  const extra = parseExtraJson(row?.extra_json);
  const raw = extra.regional_settings;
  if (!isObject(raw)) {
    return {
      ...PH_DEFAULT_REGIONAL_SETTINGS,
      money_format: { ...PH_DEFAULT_REGIONAL_SETTINGS.money_format },
      id_types: PH_DEFAULT_REGIONAL_SETTINGS.id_types.map((t) => ({ ...t })),
    };
  }
  const defaultCc = PH_DEFAULT_REGIONAL_SETTINGS.default_mobile_country_code;
  return {
    default_mobile_country_code:
      typeof raw.default_mobile_country_code === 'string' &&
      raw.default_mobile_country_code.length > 0
        ? raw.default_mobile_country_code
        : defaultCc,
    money_format: mergeMoneyFormat(raw.money_format),
    id_types: mergeIdTypes(raw.id_types),
  };
}

/** Return only the enabled ID types in their stored order. */
export function getEnabledIdTypes(regional: RegionalSettings): IdTypeEntry[] {
  return regional.id_types.filter((t) => t.enabled);
}

/** Return the default international dialing prefix (e.g. "+63"). */
export function getDefaultMobileCountryCode(regional: RegionalSettings): string {
  return regional.default_mobile_country_code;
}

/**
 * Format a major-unit number (e.g. 12500 → "₱12,500.00") using the
 * clinic's regional money_format config. Negative values are formatted
 * with a leading minus before the currency token.
 */
export function formatMoney(
  value: number | string | null | undefined,
  regional: RegionalSettings,
): string {
  const fmt = regional.money_format;
  let num: number;
  if (value == null) {
    num = 0;
  } else if (typeof value === 'string') {
    const parsed = parseFloat(value);
    num = Number.isFinite(parsed) ? parsed : 0;
  } else {
    num = Number.isFinite(value) ? value : 0;
  }
  const negative = num < 0;
  const abs = Math.abs(num);
  const fixed = abs.toFixed(Math.min(4, Math.max(0, fmt.decimal_places)));
  const [whole, frac = ''] = fixed.split('.');
  const wholeFormatted = fmt.use_thousands_separator
    ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousands_separator)
    : whole;
  const numericPart = frac ? `${wholeFormatted}${fmt.decimal_separator}${frac}` : wholeFormatted;
  const token = fmt.currency_display === 'code' ? 'PHP' : fmt.currency_symbol;
  const body =
    fmt.currency_position === 'prefix' ? `${token}${numericPart}` : `${numericPart}${token}`;
  return negative ? `-${body}` : body;
}

/**
 * Serialize `regional_settings` into an updated `extra_json` JSON string,
 * preserving every other top-level key already present.
 */
export function writeRegionalSettings(
  currentExtraJson: unknown,
  regional: RegionalSettings,
): string {
  const next = { ...parseExtraJson(currentExtraJson) };
  next.regional_settings = regional;
  return JSON.stringify(next);
}
